import {state, style, transition, trigger, useAnimation} from '@angular/animations';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {flyInEnter, flyInLeave} from "../../animations/fly";
import {IMenuItem, MenuItem} from "../../../models/menu-item";
import {SideNavService} from '../../services/side-nav.service';
import {distinctUntilChanged, filter, Subscription} from 'rxjs';
import {AuthService} from "../../services/auth.service";
import {environment} from "../../../../../environments/environment";
import { IsMobileService } from '@shared/services/is-mobile.service';

@Component({
  selector: 'sigecom-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  animations: [
    trigger('OpenMenu', [
      state('flyIn', style({transform: 'translateY(0)'})),
      transition(':enter', [useAnimation(flyInEnter)]),
      transition(':leave', [useAnimation(flyInLeave)]),
    ]),
  ],
})
export class SideNavComponent implements OnInit, OnDestroy {
  public hideSideNav: boolean = false;
  public menuItens: MenuItem[] = [];
  public isMobile: boolean = false;
  public mobileOpen: boolean = false;

  public enable: boolean = environment.enableMarquee;

  private subscription?: Subscription;
  private routerSub?: Subscription;
  private mobileSub?: Subscription;

  private readonly isMobileService = inject(IsMobileService);

  constructor(
    public router: Router,
    private sideNavService: SideNavService,
    private authService: AuthService,
  ) {
  }

  ngOnInit() {
    // Seed initial value from the service (already reads window.innerWidth)
    this.isMobile = this.isMobileService.getIsMobile();
    this.hideSideNav = this.isMobile;

    // React to every breakpoint crossing driven by the service's resize listener
    this.mobileSub = this.isMobileService.isMobile$.pipe(distinctUntilChanged()).subscribe(isMobile => {
      const wasMobile = this.isMobile;
      this.isMobile = isMobile;

      // Switching mobile → desktop: reset drawer state
      if (wasMobile && !isMobile) {
        this.mobileOpen = false;
      }
      // Switching desktop → mobile: collapse nav
      if (!wasMobile && isMobile) {
        this.mobileOpen = false;
        this.hideSideNav = true;
      }
    });

    this.subscription = this.sideNavService.isHide.subscribe(value => {
      this.hideSideNav = value;
      if (this.isMobile) {
        this.mobileOpen = !value;
      }
    });

    this.createMenu();

    // Auto-close drawer on navigation (mobile only)
    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMobile && this.mobileOpen) {
          this.closeMobileDrawer();
        }
      });
  }

  closeMobileDrawer() {
    this.mobileOpen = false;
    this.sideNavService.hide();
  }

  createMenu() {
    const perfis = this.authService.getPerfisUsuario();

    const isAdmin = perfis.includes('ROLE_CBDE');
    const isFederacao = perfis.includes('ROLE_DIRIGENTE') || perfis.includes('ROLE_DIRETOR') || perfis.includes('ROLE_PRESIDENTE_DE_FEDERACAO');
    const isVoluntario = perfis.includes('ROLE_VOLUNTARIO');
    const isSecretaria = perfis.includes('ROLE_SECRETARIA');
    const isOperacoes = perfis.includes('ROLE_OPERACOES');
    const isInstitution = perfis.includes('ROLE_REPRESENTANTE_DE_INSTITUICAO_DE_ENSINO');

    //Dashboard
    const dashboard = new MenuItem('Dashboard', ['dashboard'], 'square-poll-vertical', []);
    if (isAdmin) {
      this.addMenuItem(dashboard, []);
    }

    // Módulo de cadastro
    const cadastro = new MenuItem('Cadastro', ['cadastro'], 'users', []);
    const usuarios = new MenuItem('Usuários', ['usuarios'], 'person', []);
    const validadores = new MenuItem('Validadores', ['usuarios', 'validadores'], 'gear', []);
    const estados = new MenuItem('Estados',['estados'],'sign-hanging',[]);
    const cidades = new MenuItem('Cidades',['cidades'],'sign-hanging',[]);
    const federacoes = new MenuItem('Federações', ['federacoes'], 'sign-hanging', []);
    const instituicoes = new MenuItem('Instituições de Ensino', ['instituicoes'], 'book', []);

    // const atletas = new MenuItem('Atletas', ['atletas'], 'person-running', []);
    //const funcoes = new MenuItem('Função', ['funcoes'], 'engineering', []);

    const subItems = [];
    if (isAdmin || isOperacoes) {
      subItems.push(usuarios, validadores, estados, cidades, federacoes, instituicoes);
    } else if (isFederacao) {
      subItems.push(usuarios, instituicoes, validadores);
    } else if (isInstitution) {
      subItems.push(usuarios);
    } else if (isVoluntario || isSecretaria) {
      subItems.push(usuarios);
    }

    this.addMenuItem(cadastro, subItems);

    // Módulo de Eventos
    const eventsSubItens: MenuItem[] = [];
    const moduloEventos = new MenuItem('Eventos', ['modulo-eventos'], 'trophy', eventsSubItens);
    const cadastroEventos = new MenuItem('Cadastro de eventos', ['eventos'], 'ranking-star', []);
    const cadastroCampeonatos = new MenuItem('Cadastro de competições das modalidades', ['competicoes'], 'dumbbell', []);
    const validacoesCategorias = new MenuItem('Gerenciamento de validações', ['eventos', 'gerenciamento-validacoes'], 'scale-balanced', []);
    const funcoes = new MenuItem('Funções', ['eventos', 'listar-funcoes'], 'briefcase', []);

    if (isAdmin || isFederacao) {
      eventsSubItens.push(cadastroEventos, cadastroCampeonatos, validacoesCategorias, funcoes);
      this.menuItens.push(moduloEventos);
    }

    // Módulo de Inscrições
    const inscriptionSubItems: MenuItem[] = [];
    const moduloInscricoes = new MenuItem('Inscrições', ['modulo-inscricoes'], 'ticket', inscriptionSubItems);
    const inscricaoAtleta = new MenuItem('Inscrição de atletas', ['inscricoes/atletas'], 'id-card', []);
    const inscricaoPrestador = new MenuItem('Inscrição de dirigentes', ['inscricoes/dirigentes'], 'id-card', []);
    const inscricaoComissaoTecnica = new MenuItem('Inscrição de comissão técnica', ['inscricoes/comissao-tecnica'], 'id-card', []);
    const inscricaoColaborador = new MenuItem('Inscrição de colaboradores', ['inscricoes/colaboradores'], 'id-card', []);
    const validarTermos = new MenuItem('Validar Termos', ['inscricoes/validar-termos'], 'square-check', []);
    const pendenciasInscricoes = new MenuItem('Pendências de Inscrições', ['inscricoes/pendencias'], 'exclamation-triangle', []);

    if (isAdmin || isOperacoes) {
      inscriptionSubItems.push(inscricaoAtleta, inscricaoPrestador, inscricaoComissaoTecnica, inscricaoColaborador,validarTermos, pendenciasInscricoes);
    } else if (isFederacao || isInstitution) {
      inscriptionSubItems.push(inscricaoAtleta, inscricaoPrestador, inscricaoComissaoTecnica, pendenciasInscricoes);
    } else if (isVoluntario) {
      inscriptionSubItems.push(inscricaoPrestador);
    }

    this.menuItens.push(moduloInscricoes);

    // Módulo Serviços
    const servicesSubItens: MenuItem[] = [];
    const moduloServicos = new MenuItem('Serviços', ['modulo-servicos'], 'plate-wheat', servicesSubItens);
    const cadatroBeneficios = new MenuItem('Cadastro de Benefícios', ['beneficios'], 'utensils', []);
    const cadatroServicos = new MenuItem('Cadastro de Serviços', ['servicos'], 'receipt', []);
    const vincularServicosUsuario = new MenuItem('Serviços dos usuários', ['servicos', 'vinculo-usuario'],'cookie', [] );
    const resgistrosServicos = new MenuItem('Registros', ['registros'], 'cookie-bite', []);
    const resgistrosServicosResumo = new MenuItem('Registros Resumo', ['registros', 'resumo'], 'cookie-bite', []);
    servicesSubItens.push(cadatroBeneficios, cadatroServicos, vincularServicosUsuario, resgistrosServicos, resgistrosServicosResumo);
    if (isAdmin || isFederacao) {
      this.menuItens.push(moduloServicos);
    }

    // Módulo de relatórios
    if (!isVoluntario && !isSecretaria && !isInstitution) {
      const exportacoesSubsItens: MenuItem[] = [];
      const moduloExportacoes = new MenuItem('Exportações', ['menu-exportacoes'], 'box-archive', exportacoesSubsItens);

      const moduloRelatorios = new MenuItem('Relatórios', ['exportacoes', 'relatorios'], 'chart-pie', []);

      const aplicacoes = new MenuItem('Aplicações', ['downloads'], 'table-cells', []);

      const relatoriosAgendados = new MenuItem('Exportações agendadas', ['exportacoes', 'relatorios-agendados'], 'calendar', []);

      exportacoesSubsItens.push(relatoriosAgendados);
      exportacoesSubsItens.push(moduloRelatorios);

      if (isAdmin) {
        exportacoesSubsItens.push(aplicacoes);
      }

      // const moduloCertificados = new MenuItem('Certificados', ['exportacoes', 'certificados'], 'library_books', []);
      // exportacoesSubsItens.push(moduloCertificados);

      this.menuItens.push(moduloExportacoes);
      // this.addMenuItem(moduloExportacoes, [moduloRelatorios, moduloCertificados]);
    }

    // Módulo Competições
    const competitionsSubItens: MenuItem[] = [];
    const moduloCompeticoes = new MenuItem('Competições', ['modulo-competicoes'], 'medal', competitionsSubItens);
    const parametrizacaoEtapa = new MenuItem('Parametrização da fase', ['parametrizacao-etapa'], 'shuffle', []);
    const gerenciarRodadasComLocal = new MenuItem(
      'Gerenciar rodadas e confrontos',
      ['gerenciar-rodadas-e-confrontos'],
      'table',
      []
    );
    const parametrizacaoEsporte = new MenuItem('Parametrização do esporte', ['parametrizacao-esporte'], 'volleyball', []);
    const arenas = new MenuItem('LOCAIS DE COMPETIÇÃO', ['arenas'], 'chess-rook', [] );
    const tabelaJogos = new MenuItem('TABELA DE JOGOS',['tabela-jogos'],'calendar',[]);
    const modelosParametrizacao = new MenuItem('MODELOS DE PARAMETRIZAÇÃO',['modelos-parametrizacao'],'newspaper',[]);
    competitionsSubItens.push( parametrizacaoEtapa, gerenciarRodadasComLocal, parametrizacaoEsporte, arenas,tabelaJogos, modelosParametrizacao );
    if (isAdmin) {
      this.menuItens.push(moduloCompeticoes);
    }

  }

  addMenuItem(item: MenuItem, subItems: MenuItem[]) {
    item.addSubMenuItems(subItems)
    this.menuItens.push(item);
  }

  isActiveRoute(route: string[]) {
    const routeString = route.join("/");
    return this.router.isActive(routeString, {
      paths: 'subset',
      matrixParams: 'ignored',
      queryParams: 'ignored',
      fragment: 'ignored'
    });
  }

  activeMenu(menuItem: IMenuItem) {
    if (menuItem.subitens.length) {
      menuItem.switch!();
    } else {
      this.router.navigate(menuItem.route).then();

    }
  }

  redirectTo() {
    this.router.navigateByUrl('').then();
  }

  logout() {
    this.authService.clearCredentials();
    this.router.navigate(['/', 'login']);
  }

  profile() {
    this.router.navigate(['perfil']);
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.routerSub?.unsubscribe();
    this.mobileSub?.unsubscribe();
  }

  protected readonly environment = environment;
}

